import { useCallback, useEffect, useState } from "react";
import { Bell, CheckCheck, CheckCircle2, Info, Loader2 } from "lucide-react";
import ToastHelper from "@/utils/toastHelper";

const getValue = (item, camel, pascal) => item?.[camel] ?? item?.[pascal];

function formatTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

export default function NotificationList({ title, service, testId }) {
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const result = await service.getAll();
      setItems(result.notifications);
      setUnreadCount(result.unreadCount);
    } catch (error) {
      ToastHelper.apiError(error, "Unable to load notifications.");
    } finally {
      setLoading(false);
    }
  }, [service]);

  useEffect(() => {
    load();
  }, [load]);

  const markRead = async (item) => {
    const notificationId = getValue(item, "notificationId", "NotificationId");
    const isRead = Boolean(getValue(item, "isRead", "IsRead"));
    if (!notificationId || isRead) return;

    try {
      await service.markRead(notificationId);
      setItems((current) => current.map((notification) =>
        getValue(notification, "notificationId", "NotificationId") === notificationId
          ? { ...notification, isRead: true, IsRead: true }
          : notification
      ));
      setUnreadCount((count) => Math.max(0, count - 1));
      window.dispatchEvent(new CustomEvent("notifications:updated"));
    } catch (error) {
      ToastHelper.apiError(error, "Unable to mark notification as read.");
    }
  };

  const markAllRead = async () => {
    if (unreadCount === 0) return;
    try {
      await service.markAllRead();
      setItems((current) => current.map((notification) => ({ ...notification, isRead: true, IsRead: true })));
      setUnreadCount(0);
      window.dispatchEvent(new CustomEvent("notifications:updated"));
    } catch (error) {
      ToastHelper.apiError(error, "Unable to mark all notifications as read.");
    }
  };

  return (
    <div className="space-y-6" data-testid={testId}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-extrabold text-[#0A2540]">{title}</h1>
          <p className="text-sm text-slate-500 mt-1">{unreadCount} unread notification{unreadCount === 1 ? "" : "s"}</p>
        </div>
        <button
          type="button"
          onClick={markAllRead}
          disabled={unreadCount === 0}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-[#0A2540] hover:bg-slate-50 disabled:opacity-50"
        >
          <CheckCheck className="w-4 h-4" /> Mark all as read
        </button>
      </div>

      {loading ? (
        <div className="wc-card flex items-center justify-center py-12 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading notifications…
        </div>
      ) : items.length === 0 ? (
        <div className="wc-card text-center py-12">
          <Bell className="w-10 h-10 mx-auto text-slate-300" />
          <div className="font-semibold text-[#0A2540] mt-3">No notifications</div>
          <div className="text-sm text-slate-500 mt-1">New workflow updates will appear here.</div>
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((item) => {
            const notificationId = getValue(item, "notificationId", "NotificationId");
            const type = String(getValue(item, "notificationType", "NotificationType") || "info").toLowerCase();
            const isRead = Boolean(getValue(item, "isRead", "IsRead"));
            const success = type.includes("success") || type.includes("approved") || type.includes("completed") || type.includes("certificate") || type.includes("payment");
            return (
              <button
                type="button"
                key={notificationId}
                onClick={() => markRead(item)}
                className={`wc-card w-full flex items-start gap-3 text-left transition ${isRead ? "bg-white" : "bg-orange-50/40 border-orange-200"}`}
                data-testid={`notification-${notificationId}`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${success ? "bg-emerald-50 text-emerald-600" : "bg-blue-50 text-blue-600"}`}>
                  {success ? <CheckCircle2 className="w-5 h-5" /> : <Info className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="font-semibold text-[#0A2540]">{getValue(item, "title", "Title")}</div>
                    <div className="text-xs text-slate-400 shrink-0">{formatTime(getValue(item, "notificationDt", "NotificationDt"))}</div>
                  </div>
                  <div className="text-sm text-slate-600 mt-1">{getValue(item, "message", "Message")}</div>
                  {!isRead && <div className="text-xs font-semibold text-orange-600 mt-2">New</div>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
